/**
 * TradeFinder display + sound preferences (this browser only). The push
 * alert rules live on the engine (flowAlertsStore), not here.
 *
 * Persisted as `tradefinder-ux`; the merge is per key so a setting added
 * later falls back to its default instead of being lost to a shallow merge,
 * and malformed saved values are dropped.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ALERT_KINDS, DEFAULT_LEVEL_TOL, TF_ROOTS, type AlertKind } from '../components/TradeFinder/tradeFinderUx';

export interface TradeFinderSound {
  enabled: boolean;
  /** 0..1. */
  volume: number;
  /** Per-kind chime, default off. */
  kinds: Record<AlertKind, boolean>;
}

export interface TradeFinderUxPrefs {
  /** Latest-signals strip over the chart. */
  stripEnabled: boolean;
  /** Signals in the strip (1..6). */
  stripCount: number;
  /** Larger glyph + glow + "🍪 at PW" when a marker sits on a key level. */
  levelEmphasis: boolean;
  /** Points per root. */
  levelTol: Record<string, number>;
  /**
   * Kinds whose MEASURED edge is weak (|band t| < 2 for that kind and symbol)
   * drawn at reduced opacity. Missing edge data never dims. Default off until
   * the tuner publishes edges for every kind.
   */
  dimNoEdge: boolean;
  /** Older markers fade towards a floor. */
  fade: boolean;
  /** Minutes at full strength before the fade starts (1..60). */
  fadeStartMin: number;
  /** The newest marker pulses briefly. */
  pulse: boolean;
  sound: TradeFinderSound;
}

interface TradeFinderUxState extends TradeFinderUxPrefs {
  set: (patch: Partial<Omit<TradeFinderUxPrefs, 'sound' | 'levelTol'>>) => void;
  setLevelTol: (root: string, pts: number) => void;
  setSound: (patch: Partial<Omit<TradeFinderSound, 'kinds'>>) => void;
  setSoundKind: (kind: AlertKind, on: boolean) => void;
}

export const STRIP_COUNT_MAX = 6;

const allKinds = (on: boolean) => Object.fromEntries(ALERT_KINDS.map((k) => [k, on])) as Record<AlertKind, boolean>;

export const DEFAULT_TF_UX: TradeFinderUxPrefs = {
  stripEnabled: true,
  stripCount: 3,
  levelEmphasis: true,
  levelTol: { ...DEFAULT_LEVEL_TOL },
  dimNoEdge: false,
  fade: true,
  fadeStartMin: 10,
  pulse: true,
  sound: { enabled: false, volume: 0.5, kinds: allKinds(false) },
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Saved state (any version, any shape) onto the defaults, key by key. */
export function mergeTradeFinderUx(persisted: unknown, current: TradeFinderUxPrefs): TradeFinderUxPrefs {
  const p = (persisted && typeof persisted === 'object' ? persisted : {}) as Record<string, unknown>;
  const out: TradeFinderUxPrefs = {
    ...current,
    levelTol: { ...current.levelTol },
    sound: { ...current.sound, kinds: { ...current.sound.kinds } },
  };
  for (const k of ['stripEnabled', 'levelEmphasis', 'dimNoEdge', 'fade', 'pulse'] as const) {
    if (typeof p[k] === 'boolean') out[k] = p[k] as boolean;
  }
  if (isNum(p.stripCount)) out.stripCount = clamp(Math.round(p.stripCount), 1, STRIP_COUNT_MAX);
  if (isNum(p.fadeStartMin)) out.fadeStartMin = clamp(Math.round(p.fadeStartMin), 1, 60);
  if (p.levelTol && typeof p.levelTol === 'object') {
    for (const root of TF_ROOTS) {
      const v = (p.levelTol as Record<string, unknown>)[root];
      if (isNum(v)) out.levelTol[root] = clamp(v, 0, 50);
    }
  }
  const s = p.sound;
  if (s && typeof s === 'object') {
    const so = s as Record<string, unknown>;
    if (typeof so.enabled === 'boolean') out.sound.enabled = so.enabled;
    if (isNum(so.volume)) out.sound.volume = clamp(so.volume, 0, 1);
    if (so.kinds && typeof so.kinds === 'object') {
      for (const k of ALERT_KINDS) {
        const v = (so.kinds as Record<string, unknown>)[k];
        if (typeof v === 'boolean') out.sound.kinds[k] = v;
      }
    }
  }
  return out;
}

/** Persisted state from `version` -> the current version's shape. */
export function migrateTradeFinderUx(persisted: unknown, version: number): unknown {
  if (!persisted || typeof persisted !== 'object') return persisted;
  if (version < 2) return { ...(persisted as Record<string, unknown>), dimNoEdge: false };
  return persisted;
}

export const useTradeFinderUxStore = create<TradeFinderUxState>()(
  persist(
    (set) => ({
      ...DEFAULT_TF_UX,
      levelTol: { ...DEFAULT_TF_UX.levelTol },
      sound: { ...DEFAULT_TF_UX.sound, kinds: { ...DEFAULT_TF_UX.sound.kinds } },
      set: (patch) =>
        set(() => {
          const next: Partial<TradeFinderUxPrefs> = { ...patch };
          if (next.stripCount !== undefined) next.stripCount = clamp(Math.round(next.stripCount), 1, STRIP_COUNT_MAX);
          if (next.fadeStartMin !== undefined) next.fadeStartMin = clamp(Math.round(next.fadeStartMin) || 1, 1, 60);
          return next;
        }),
      setLevelTol: (root, pts) =>
        set((s) => (Number.isFinite(pts) ? { levelTol: { ...s.levelTol, [root]: clamp(pts, 0, 50) } } : s)),
      setSound: (patch) =>
        set((s) => ({
          sound: {
            ...s.sound,
            ...patch,
            volume: patch.volume !== undefined ? clamp(patch.volume, 0, 1) : s.sound.volume,
          },
        })),
      setSoundKind: (kind, on) => set((s) => ({ sound: { ...s.sound, kinds: { ...s.sound.kinds, [kind]: on } } })),
    }),
    {
      name: 'tradefinder-ux',
      version: 2,
      partialize: (s) => ({
        stripEnabled: s.stripEnabled,
        stripCount: s.stripCount,
        levelEmphasis: s.levelEmphasis,
        levelTol: s.levelTol,
        dimNoEdge: s.dimNoEdge,
        fade: s.fade,
        fadeStartMin: s.fadeStartMin,
        pulse: s.pulse,
        sound: s.sound,
      }),
      // v1 saved dimNoEdge = true as the old default (it dimmed kinds with no
      // edge data at all); v2 defaults it off. Any other shape is folded in key
      // by key by merge below.
      migrate: (persisted, version) => migrateTradeFinderUx(persisted, version) as TradeFinderUxState,
      merge: (persisted, current) => ({ ...current, ...mergeTradeFinderUx(persisted, current) }),
    },
  ),
);
